import type {JSX} from 'react';
import {useState} from 'react';

import {Button, DatePicker, Input} from '@/components';
import {Filter} from '@/components/Filter';
import type {AfriexLogParams} from '@/types';
import {AfriexErrorCode} from '@/types';

// Error logs are written by ErrorRenderer from core's AfriexError, so meta.code
// holds an AfriexErrorCode. Offering them as a datalist gives the filter a real
// vocabulary to pick from; it stays a free-text input on purpose, so an
// operator can still search a code this enum does not cover.
const ERROR_CODE_OPTIONS = Object.values(AfriexErrorCode);
const ERROR_CODE_LIST_ID = 'merchant-logs-error-codes';

type MerchantLogsFiltersProps = {
  filterCount: number;
  filtersToApply: AfriexLogParams;
  clearFilters: () => void;
  selectAppliedFilters: (
    name: string,
    value: string | Date | undefined,
  ) => void;
  /** Returns whether the filters were applied; false when validation rejected them. */
  handleApplyFilters: () => boolean;
};

export default function MerchantLogsFilters({
  filterCount,
  filtersToApply,
  clearFilters,
  selectAppliedFilters,
  handleApplyFilters,
}: MerchantLogsFiltersProps): JSX.Element {
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const fromDate = filtersToApply.fromDate
    ? new Date(filtersToApply.fromDate)
    : undefined;

  const toDate = filtersToApply.toDate
    ? new Date(filtersToApply.toDate)
    : undefined;

  const handleShowFilterChange = (): void => {
    setShowFilters(!showFilters);
  };

  const handleApplyFilter = (): void => {
    // Only close on success. Dismissing the panel after a rejected date range
    // hid the values the user has to correct. See hooks/useFilters.ts.
    if (handleApplyFilters()) {
      setShowFilters(false);
    }
  };

  const handleClearFilters = (): void => {
    clearFilters();
  };

  return (
    <div className="mt-5 w-full">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex items-center space-x-3">
          <Filter
            closeFilter={() => setShowFilters(false)}
            handleShowFilter={handleShowFilterChange}
            label="Filters"
            positionLeft={1}
            data-cy="merchant-logs-filter-toggle"
            bodyClassName="w-full md:w-[600px]"
          >
            {showFilters && (
              <div
                className="p-4 space-y-4 max-w-4xl mx-auto w-full col-span-full"
                data-cy="merchant-logs-filter-panel"
              >
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col md:flex-row items-center gap-2">
                    <Input
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        selectAppliedFilters('businessId', e.target.value)
                      }
                      name="businessId"
                      data-cy="merchant-logs-filter-business-id"
                      label="Business ID"
                      type="text"
                      value={filtersToApply.businessId ?? ''}
                      className="w-full isFull"
                      placeholder="Enter Business ID"
                    />
                    <Input
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        selectAppliedFilters('userId', e.target.value)
                      }
                      name="userId"
                      data-cy="merchant-logs-filter-user-id"
                      label="User ID"
                      type="text"
                      value={filtersToApply.userId ?? ''}
                      className="w-full isFull"
                      placeholder="Enter User ID"
                    />
                  </div>

                  <div className="flex flex-col md:flex-row items-center gap-2">
                    <Input
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        selectAppliedFilters('errorCode', e.target.value)
                      }
                      name="errorCode"
                      data-cy="merchant-logs-filter-error-code"
                      label="Error Code"
                      type="text"
                      value={filtersToApply.errorCode ?? ''}
                      className="w-full isFull"
                      placeholder="e.g. ACCOUNT_NOT_FOUND"
                      list={ERROR_CODE_LIST_ID}
                    />
                    <datalist id={ERROR_CODE_LIST_ID}>
                      {ERROR_CODE_OPTIONS.map((code) => (
                        <option key={code} value={code} />
                      ))}
                    </datalist>
                    <Input
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        selectAppliedFilters('errorMessage', e.target.value)
                      }
                      name="errorMessage"
                      data-cy="merchant-logs-filter-error-message"
                      label="Error Message"
                      type="text"
                      value={filtersToApply.errorMessage ?? ''}
                      className="w-full isFull"
                      placeholder="Error message starts with..."
                    />
                  </div>

                  <div className="flex flex-col md:flex-row gap-2">
                    {/* react-datepicker only forwards a whitelist of props to its
                        input, so data-cy goes on a wrapper. */}
                    <div data-cy="merchant-logs-filter-from-date">
                      <DatePicker
                        label="From Date"
                        selectedDate={fromDate}
                        setSelectedDate={(date) =>
                          selectAppliedFilters('fromDate', date)
                        }
                        placeholderText="Select Date"
                      />
                    </div>
                    <div data-cy="merchant-logs-filter-to-date">
                      <DatePicker
                        label="To Date"
                        selectedDate={toDate}
                        setSelectedDate={(date) =>
                          selectAppliedFilters('toDate', date)
                        }
                        placeholderText="Select Date"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex md:justify-end">
                  <Button
                    onClick={handleClearFilters}
                    colorScheme="cyan"
                    variant="ghost"
                    data-cy="merchant-logs-filter-clear"
                  >
                    Clear
                  </Button>

                  <Button
                    onClick={handleApplyFilter}
                    colorScheme="cyan"
                    disabled={filterCount === 0}
                    className="ml-3"
                    data-cy="merchant-logs-filter-apply"
                  >
                    Apply Filters {filterCount ? ` (${filterCount})` : ''}
                  </Button>
                </div>
              </div>
            )}
          </Filter>
        </div>
      </div>
    </div>
  );
}
